import React, { ReactElement } from 'react';

import { Text, VStack, Flex, HStack, rem, Link } from '@vimeo/bokeh';
import { shouldShowInDevelopmentFeature } from '@vimeo/query-feature-flag';

import { Categories } from './components/Categories/Categories';
import { CreativeCommons } from './components/CreativeCommons/CreativeCommons';
import { Credits } from './components/Credits/Credits';
import { Description } from './components/Description/Description';
import { Tags } from './components/Tags/Tags';
import { TeamUploadedBy } from './components/TeamUploadedBy/TeamUploadedBy';
import { VideoBadges } from './components/VideoBadges/VideoBadges';
import { VideoDetailsType } from './types';

/**
 * Pure presentational UI for the Video Details section
 */
export function VideoDetailsBase({
  language,
  title = 'Video title',
  createdTime,
  timeSinceCreatedString,
  viewsString,
  likesString,
  creativeCommonsDescriptor,
  description,
  credits,
  categories,
  tags,
  uploader,
  manageButton,
  aiContent,
  createdOnString,
  showTagLinks = false,
  /** Flags */
  is360Video,
  isAdvert,
  isCategoriesLinkEnabled = false,
  isDolbyVision,
  isHdr10,
  isHdr10Plus,
  isRatedMature,
  isStreaming,
  isLiveEvent,
  /** Visibility Controls */
  showCategories,
  showCreativeCommons,
  showCredits,
  showDateAdded,
  showDescription,
  showLike,
  showTags,
  showTitle,
  showUploadedBy,
  showViews,
  showManageButton,
  children,
  /** Callbacks */
  onCreditedProfilesLinkClick,
  onCategoriesLinkClick,
  onMomentPlay,
  onLikesListClick,
}: React.PropsWithChildren<VideoDetailsType>): ReactElement {
  const createdOn = showDateAdded ? (
    <Text variant="body-md" color="text-secondary" mb={50}>
      {createdOnString}
    </Text>
  ) : null;

  const shouldShowViews = showViews && viewsString;
  const shouldShowDataAdded = showDateAdded && createdTime && timeSinceCreatedString;
  const shouldShowLikesModal =
    shouldShowInDevelopmentFeature('likes_modal', true) && likesString && showLike;

  return (
    <VStack
      align="stretch"
      data-testid="vd-wrapper"
      p={{ base: 200, md: 300 }}
      spacing={200}
      overflow="hidden"
      lang={language || ''}
    >
      {showTitle ? (
        <Flex justify="space-between" gap="200" mb={rem(-8)}>
          <Text
            as="h1"
            variant={{ base: 'heading-md', md: 'heading-lg', xl: 'heading-xl' }}
            minW="0"
            style={{ hyphens: 'auto' }}
          >
            {title}
          </Text>
          {showManageButton ? manageButton : null}
        </Flex>
      ) : null}
      <Flex gap={100} flexWrap="wrap">
        {!isLiveEvent && (shouldShowViews || shouldShowDataAdded) ? (
          <Text variant="body-xl" fontSize="text-sm" color="text-secondary">
            {shouldShowViews ? <span data-testid="views">{viewsString}</span> : null}
            {shouldShowViews && shouldShowDataAdded && createdTime !== undefined ? (
              <Text variant="body-xl" as="span" mx={50} aria-hidden="true">
                &middot;
              </Text>
            ) : null}
            {shouldShowDataAdded ? (
              <time dateTime={createdTime}>{timeSinceCreatedString}</time>
            ) : null}
            {shouldShowDataAdded && shouldShowLikesModal ? (
              <Text variant="body-xl" as="span" mx={50} aria-hidden="true">
                &middot;
              </Text>
            ) : null}
            {shouldShowLikesModal ? (
              <Link
                href="#"
                data-testid="likes-modal"
                fontSize="text-sm"
                variant="secondary"
                onClick={onLikesListClick}
              >
                {likesString}
              </Link>
            ) : null}
          </Text>
        ) : null}
        <HStack flexWrap="wrap" gap={50}>
          <VideoBadges
            isStreaming={isStreaming}
            isRatedMature={isRatedMature}
            is360Video={is360Video}
            isDolbyVision={isDolbyVision}
            isHdr10={isHdr10}
            isHdr10Plus={isHdr10Plus}
            aiContent={aiContent}
            isAdvert={isAdvert}
          />
        </HStack>
      </Flex>
      {children}
      {showDescription && description ? (
        <Description onMomentPlay={onMomentPlay} description={description}>
          {createdOn}
        </Description>
      ) : (
        createdOn
      )}
      {showTags && tags?.length ? <Tags tags={tags} showLinks={showTagLinks} /> : null}
      {showCredits && credits?.length ? (
        <Credits credits={credits} onClick={onCreditedProfilesLinkClick} />
      ) : null}
      {showCategories && categories?.length ? (
        <Categories
          categories={categories}
          onClick={onCategoriesLinkClick}
          isCategoriesLinkEnabled={isCategoriesLinkEnabled}
        />
      ) : null}
      {showCreativeCommons && creativeCommonsDescriptor ? (
        <CreativeCommons creativeCommonsDescriptor={creativeCommonsDescriptor} />
      ) : null}
      {showUploadedBy && uploader ? <TeamUploadedBy uploader={uploader} /> : null}
    </VStack>
  );
}
